/* Per-channel edit dialog — extracted from Dashboard.jsx during the 2026-02-08
   refactor. Owns the full risk-profile form, Telegram-dialog picker for
   inbound source chats, and the destructive-delete confirmation flow. */
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { channels, signals, telegram, trades, trading } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge, cn, Kv } from "@/components/atoms";
import { ToggleRow, SliderRow } from "@/components/dashboard/formfields";

export default function ChannelEditDialog({ channel, telegramSourceId, onClose, onSaved }) {
  const isNew = !channel;
  // Full risk profile — mirrors backend `RiskSettings` fields so channel-scoped
  // overrides are 1:1 with the legacy global card. Any field left at its
  // sentinel default gets merged with the user-level `risk_settings` at
  // dispatch time (see trade_engine.process_signal).
  const rp = channel?.risk_profile || {};
  const [form, setForm] = useState({
    name: channel?.name || "",
    exchange: channel?.exchange || "weex",
    enabled: channel?.enabled ?? true,
    telegram_chat_id: channel?.telegram_chat_id || "",
    telegram_chat_label: channel?.telegram_chat_label || "",
    // Signal execution flags
    auto_trading: rp.auto_trading ?? false,
    follow_provider_leverage: rp.follow_provider_leverage ?? true,
    execute_without_provider_sl: rp.execute_without_provider_sl ?? true,
    allow_provider_sl_replace: rp.allow_provider_sl_replace ?? true,
    allow_wider_provider_sl: rp.allow_wider_provider_sl ?? false,
    close_full_at_tp1: rp.close_full_at_tp1 ?? true,
    require_manual_approval_ai_signals: rp.require_manual_approval_ai_signals ?? false,
    auto_close_roi_pct: rp.auto_close_roi_pct ?? 0,
    ignore_provider_tp: rp.ignore_provider_tp ?? false,
    use_implied_tp_zone_when_no_explicit_tp: rp.use_implied_tp_zone_when_no_explicit_tp ?? false,
    sl_to_breakeven_at_roi_pct: rp.sl_to_breakeven_at_roi_pct ?? 0,
    sl_to_breakeven_offset_pct: rp.sl_to_breakeven_offset_pct ?? 0,
    enable_chart_ocr: rp.enable_chart_ocr ?? false,
    // Sizing / leverage
    position_sizing_mode: rp.position_sizing_mode ?? "risk",
    risk_percent_per_trade: rp.risk_percent_per_trade ?? 1.0,
    margin_percent_per_trade: rp.margin_percent_per_trade ?? 5.0,
    fixed_margin_usdt: rp.fixed_margin_usdt ?? 50,
    default_leverage: rp.default_leverage ?? 3,
    max_leverage: rp.max_leverage ?? 10,
    internal_fallback_sl_percent: rp.internal_fallback_sl_percent ?? 2.0,
    // Isolated margin (WEEX only — see weex_client.py's set_leverage v3
    // branch) and virtual per-channel balance sizing. Both opt-in,
    // default-off so existing channels are unaffected. virtual_current_balance
    // and virtual_balance_paused are server-maintained (updated on every
    // trade close via _apply_channel_virtual_pnl) — read here for DISPLAY
    // only, never sent back on a normal Save (see save mutation below), so
    // saving unrelated fields can't clobber accumulated real P&L.
    virtual_balance_enabled: rp.virtual_balance_enabled ?? false,
    virtual_starting_balance: rp.virtual_starting_balance ?? 100,
    virtual_current_balance: rp.virtual_current_balance ?? null,
    virtual_balance_paused: rp.virtual_balance_paused ?? false,
    // Go-live safety / circuit breakers
    hard_leverage_ceiling: rp.hard_leverage_ceiling ?? 25,
    max_concurrent_positions: rp.max_concurrent_positions ?? 5,
    max_notional_per_trade_usdt: rp.max_notional_per_trade_usdt ?? 0,
    daily_loss_pct_kill_switch: rp.daily_loss_pct_kill_switch ?? 0,
    symbol_cooldown_seconds: rp.symbol_cooldown_seconds ?? 0,
    dedupe_window_seconds: rp.dedupe_window_seconds ?? 600,
    max_entry_slippage_pct: rp.max_entry_slippage_pct ?? 0.3,
    place_exchange_sl_order: rp.place_exchange_sl_order ?? true,
    telegram_alerts_enabled: rp.telegram_alerts_enabled ?? true,
    skip_if_sl_beyond_liquidation: rp.skip_if_sl_beyond_liquidation ?? false,
    replace_pending_limit_on_new_signal: rp.replace_pending_limit_on_new_signal ?? false,
    entry_sl_band_gate_enabled: rp.entry_sl_band_gate_enabled ?? false,
    entry_sl_band_buffer_pct: rp.entry_sl_band_buffer_pct ?? 0.3,
    // Layered entry (EP1/EP2 DCA) + TP waterfall - no dedicated individual
    // controls yet (only set via the Trade Sensei Strategy preset below);
    // still round-tripped so a saved preset reloads correctly.
    layered_entry_enabled: rp.layered_entry_enabled ?? false,
    layered_entry_pct1: rp.layered_entry_pct1 ?? 70,
    tp_waterfall_enabled: rp.tp_waterfall_enabled ?? false,
    tp1_close_pct: rp.tp1_close_pct ?? 50,
    tp2_close_pct: rp.tp2_close_pct ?? 30,
    sl_trail_to_be_at_tp1: rp.sl_trail_to_be_at_tp1 ?? false,
    sl_trail_to_tp1_at_tp2: rp.sl_trail_to_tp1_at_tp2 ?? false,
    skip_if_tp1_already_hit: rp.skip_if_tp1_already_hit ?? false,
    // Only set on channels using the "Trade Sensei Strategy V3.2" variant -
    // replaces just the TP1 leg with a synthetic ROI-derived price, keeping
    // TP2/TP3 as the provider's real levels. Previously backend-only (no UI
    // control existed, and it wasn't even in the save payload below) -
    // exposed here so it round-trips correctly through this dialog.
    tp1_custom_roi_pct: rp.tp1_custom_roi_pct ?? null,
    ignore_provider_tp_always: rp.ignore_provider_tp_always ?? false,
    // Was previously set ONLY by preset onClick handlers and never
    // initialized from the existing doc - now that it's included in the
    // save payload above, it must be seeded here too, or saving any
    // channel WITHOUT clicking a preset button first would silently flip
    // this to false for channels (GVRCALLS, Trade Sensei Premium) that
    // already have it true in the DB.
    auto_delever_to_fit_sl: rp.auto_delever_to_fit_sl ?? false,
    // Marks the channel as locked into a named strategy preset - when set,
    // the fields that preset owns render as a read-only summary instead of
    // individually-editable controls, so a stray click can't quietly drift
    // the channel off the exact config that was actually backtested.
    strategy_preset: rp.strategy_preset ?? null,
  });
  const [dialogs, setDialogs] = useState(null);
  const [dialogSearch, setDialogSearch] = useState("");

  const loadDialogs = useMutation({
    mutationFn: () => telegram.dialogs(),
    onSuccess: (r) => {
      setDialogs(r.data || []);
      toast.success(`Loaded ${r.data.length} chats`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed to load chats"),
  });

  const filteredDialogs = (dialogs || []).filter((d) =>
    !dialogSearch ||
    (d.name || "").toLowerCase().includes(dialogSearch.toLowerCase()) ||
    (d.username || "").toLowerCase().includes(dialogSearch.toLowerCase())
  );

  const save = useMutation({
    mutationFn: () => {
      const risk_profile = {
        // Signal execution
        auto_trading: !!form.auto_trading,
        follow_provider_leverage: !!form.follow_provider_leverage,
        execute_without_provider_sl: !!form.execute_without_provider_sl,
        allow_provider_sl_replace: !!form.allow_provider_sl_replace,
        allow_wider_provider_sl: !!form.allow_wider_provider_sl,
        close_full_at_tp1: !!form.close_full_at_tp1,
        require_manual_approval_ai_signals: !!form.require_manual_approval_ai_signals,
        auto_close_roi_pct: Number(form.auto_close_roi_pct),
        ignore_provider_tp: !!form.ignore_provider_tp,
        use_implied_tp_zone_when_no_explicit_tp: !!form.use_implied_tp_zone_when_no_explicit_tp,
        sl_to_breakeven_at_roi_pct: Number(form.sl_to_breakeven_at_roi_pct),
        sl_to_breakeven_offset_pct: Number(form.sl_to_breakeven_offset_pct),
        enable_chart_ocr: !!form.enable_chart_ocr,
        // Sizing / leverage
        position_sizing_mode: ["margin", "fixed_margin"].includes(form.position_sizing_mode)
          ? form.position_sizing_mode : "risk",
        risk_percent_per_trade: Number(form.risk_percent_per_trade),
        margin_percent_per_trade: Number(form.margin_percent_per_trade),
        fixed_margin_usdt: Number(form.fixed_margin_usdt),
        default_leverage: Number(form.default_leverage),
        max_leverage: Number(form.max_leverage),
        internal_fallback_sl_percent: Number(form.internal_fallback_sl_percent),
        virtual_balance_enabled: !!form.virtual_balance_enabled,
        virtual_starting_balance: Number(form.virtual_starting_balance) || 0,
        // virtual_current_balance is server-maintained (incremented on every
        // trade close via _apply_channel_virtual_pnl) — deliberately left
        // OUT of a normal save so it can't get clobbered back to a stale
        // value. The channels PATCH endpoint shallow-merges risk_profile
        // (see server.py), so omitting a key leaves the DB's current value
        // untouched. The ONE exception: the very first time this feature is
        // turned on for this channel (rp.virtual_current_balance was never
        // set), Mongo's $inc has no prior value to add onto, so it must be
        // seeded here or the first trade close would set the balance to
        // just that trade's raw P&L instead of starting_balance + P&L.
        ...(form.virtual_balance_enabled && rp.virtual_current_balance == null
          ? { virtual_current_balance: Number(form.virtual_starting_balance) || 0 }
          : {}),
        // Go-live safety
        hard_leverage_ceiling: Number(form.hard_leverage_ceiling),
        max_concurrent_positions: Number(form.max_concurrent_positions),
        max_notional_per_trade_usdt: Number(form.max_notional_per_trade_usdt),
        daily_loss_pct_kill_switch: Number(form.daily_loss_pct_kill_switch),
        symbol_cooldown_seconds: Number(form.symbol_cooldown_seconds),
        dedupe_window_seconds: Number(form.dedupe_window_seconds),
        max_entry_slippage_pct: Number(form.max_entry_slippage_pct),
        place_exchange_sl_order: !!form.place_exchange_sl_order,
        telegram_alerts_enabled: !!form.telegram_alerts_enabled,
        skip_if_sl_beyond_liquidation: !!form.skip_if_sl_beyond_liquidation,
        replace_pending_limit_on_new_signal: !!form.replace_pending_limit_on_new_signal,
        entry_sl_band_gate_enabled: !!form.entry_sl_band_gate_enabled,
        entry_sl_band_buffer_pct: Number(form.entry_sl_band_buffer_pct) || 0.3,
        layered_entry_enabled: !!form.layered_entry_enabled,
        layered_entry_pct1: Number(form.layered_entry_pct1),
        tp_waterfall_enabled: !!form.tp_waterfall_enabled,
        tp1_close_pct: Number(form.tp1_close_pct),
        tp2_close_pct: Number(form.tp2_close_pct),
        sl_trail_to_be_at_tp1: !!form.sl_trail_to_be_at_tp1,
        sl_trail_to_tp1_at_tp2: !!form.sl_trail_to_tp1_at_tp2,
        skip_if_tp1_already_hit: !!form.skip_if_tp1_already_hit,
        ignore_provider_tp_always: !!form.ignore_provider_tp_always,
        auto_delever_to_fit_sl: !!form.auto_delever_to_fit_sl,
        ...(form.tp1_custom_roi_pct != null
          ? { tp1_custom_roi_pct: Number(form.tp1_custom_roi_pct) }
          : {}),
        strategy_preset: form.strategy_preset || null,
      };
      const body = {
        name: form.name,
        enabled: form.enabled,
        telegram_chat_id: form.telegram_chat_id || null,
        telegram_chat_label: form.telegram_chat_label || null,
        risk_profile,
      };
      if (isNew) {
        if (!telegramSourceId) {
          throw new Error("Configure Telegram in Setup first — no source available.");
        }
        return channels.create({
          ...body,
          telegram_source_id: telegramSourceId,
          exchange: form.exchange,
        });
      }
      return channels.update(channel.id, body);
    },
    onSuccess: () => { toast.success(isNew ? "Channel created" : "Channel updated"); onSaved(); },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Save failed"),
  });

  // Separate, explicit mutation for resetting/topping-up the virtual
  // balance — deliberately isolated from the main Save button so a normal
  // form edit can never accidentally reset accumulated real P&L (see the
  // comment on virtual_current_balance in the save mutation above).
  const resetVirtualBalanceMutation = useMutation({
    mutationFn: (newBalance) => channels.update(channel.id, {
      risk_profile: { virtual_current_balance: newBalance, virtual_balance_paused: false },
    }),
    onSuccess: (_r, newBalance) => {
      toast.success(`Virtual balance reset to $${Number(newBalance).toFixed(2)}`);
      setForm((prev) => ({ ...prev, virtual_current_balance: newBalance, virtual_balance_paused: false }));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Reset failed"),
  });
  const resetVirtualBalance = (newBalance) => resetVirtualBalanceMutation.mutate(newBalance);

  const setF = (k) => (v) => setForm((prev) => ({ ...prev, [k]: v }));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="channel-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {isNew ? "New channel" : `Edit: ${channel.name}`}
          </DialogTitle>
          <DialogDescription>
            Route a Telegram source to a specific exchange with its own risk profile.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Channel name</Label>
            <Input data-testid="ch-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. MoonSignals Premium" />
          </div>
          <div>
            <Label>Exchange</Label>
            <Select
              value={form.exchange}
              onValueChange={(v) => setForm({ ...form, exchange: v })}
              disabled={!isNew}
            >
              <SelectTrigger data-testid="ch-exchange" className="w-full h-10 bg-white">
                <SelectValue placeholder="Select exchange" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weex" data-testid="ch-exchange-weex">WEEX</SelectItem>
                <SelectItem value="bitget" data-testid="ch-exchange-bitget">Bitget</SelectItem>
                <SelectItem value="blofin" data-testid="ch-exchange-blofin">Blofin</SelectItem>
                <SelectItem value="gate" data-testid="ch-exchange-gate">Gate</SelectItem>
              </SelectContent>
            </Select>
            {!isNew && <p className="text-[11px] text-slate-500 mt-1">Exchange cannot be changed after create.</p>}
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label>Status</Label>
              <div className="flex items-center gap-2 h-10">
                <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="ch-enabled" />
                <span className="text-sm text-slate-700">{form.enabled ? "Enabled" : "Paused"}</span>
              </div>
            </div>
          </div>

          {/* Non-Telegram-sourced channels (e.g. GVRCALLS, ingested directly from an
              X/Twitter scraper pipeline) have no telegram_source_id at all — this
              picker is meaningless for them and was confusing to see. Only hidden for
              EXISTING channels that already lack one; new channels still default to
              Telegram since that's the only supported creation flow today. */}
          {(isNew || channel?.telegram_source_id) && (
          <div className="col-span-2 rounded-md border border-slate-200 bg-slate-50/60 p-3">
            <div className="flex items-center justify-between mb-2">
              <div>
                <Label className="text-slate-700">Telegram channel</Label>
                <p className="text-[11px] text-slate-500">
                  Which Telegram chat this row listens to. Leave blank to accept any chat (single-channel legacy mode).
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                type="button"
                onClick={() => loadDialogs.mutate()}
                disabled={loadDialogs.isPending}
                data-testid="ch-load-dialogs"
                className="text-[#4F46E5] hover:bg-slate-100 h-7 px-2 text-xs"
              >
                {loadDialogs.isPending ? "Loading…" : dialogs ? "Refresh" : "Load my chats"}
              </Button>
            </div>
            {form.telegram_chat_label ? (
              <div className="flex items-center justify-between rounded-md bg-white border border-slate-200 px-3 py-2 mb-2"
                   data-testid="ch-tg-selected">
                <div className="min-w-0">
                  <p className="text-sm text-slate-900 truncate">{form.telegram_chat_label}</p>
                  <p className="text-[10px] text-slate-500 mono truncate">id {form.telegram_chat_id}</p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  type="button"
                  data-testid="ch-tg-clear"
                  onClick={() => setForm({ ...form, telegram_chat_id: "", telegram_chat_label: "" })}
                >
                  Clear
                </Button>
              </div>
            ) : (
              <div className="text-[11px] text-slate-500 mb-2">
                No chat selected yet.
              </div>
            )}
            {dialogs && dialogs.length > 0 && (
              <>
                <Input
                  data-testid="ch-tg-search"
                  placeholder="Search…"
                  value={dialogSearch}
                  onChange={(e) => setDialogSearch(e.target.value)}
                  className="bg-white border-slate-200 h-9 mb-2 text-sm"
                />
                <div className="max-h-52 overflow-y-auto scrollbar-thin space-y-1"
                     data-testid="ch-tg-list">
                  {filteredDialogs.map((d) => {
                    const selected = String(form.telegram_chat_id) === String(d.id);
                    return (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => setForm({
                          ...form,
                          telegram_chat_id: String(d.id),
                          telegram_chat_label: d.name || d.username || String(d.id),
                        })}
                        data-testid={`ch-tg-pick-${d.id}`}
                        className={cn(
                          "w-full text-left p-2 rounded-md border transition-colors",
                          selected
                            ? "bg-[#4F46E5]/10 border-[#4F46E5]/40"
                            : "bg-white border-slate-200 hover:bg-slate-100"
                        )}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-900 truncate">{d.name}</p>
                            <p className="text-[10px] text-slate-500 mono truncate">
                              {d.username ? `@${d.username}` : `id ${d.id}`}
                            </p>
                          </div>
                          <Badge variant={d.type === "channel" ? "info" : "muted"}>{d.type}</Badge>
                        </div>
                      </button>
                    );
                  })}
                  {filteredDialogs.length === 0 && (
                    <p className="text-xs text-slate-500 text-center py-3">No matches</p>
                  )}
                </div>
              </>
            )}
            {dialogs === null && (
              <p className="text-[11px] text-slate-500">
                Click <span className="text-slate-900">Load my chats</span> to browse the Telegram channels/groups you&apos;re in.
              </p>
            )}
          </div>
          )}

          <div className="col-span-2 mt-2 pt-4 border-t border-slate-200">
            <p className="overline">Risk & Guardrails</p>
            <p className="text-[11px] text-slate-500">Channel-scoped overrides. Any field left unchanged inherits the global risk defaults on Save.</p>
          </div>

          {form.strategy_preset === "trade_talks" && (
            <div className="col-span-2 rounded-md border border-emerald-300 bg-emerald-50 p-3 space-y-2"
                 data-testid="ch-strategy-locked-summary">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-emerald-900">Trade Talks Strategy — active</p>
                <Button
                  type="button" size="sm" variant="ghost"
                  className="h-6 px-2 text-[11px] text-emerald-700 hover:bg-emerald-100"
                  data-testid="ch-unlock-strategy"
                  onClick={() => setForm((prev) => ({ ...prev, strategy_preset: null }))}
                >
                  Unlock to customize
                </Button>
              </div>
              <p className="text-[11px] text-emerald-800">
                These fields are locked to the backtested config so they can't drift by accident.
                Unlock to edit them individually — the settings below (auto-trading, chart OCR,
                fallback SL%, circuit breakers) are still yours to adjust either way.
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono text-emerald-900 pt-1 border-t border-emerald-200">
                <span>Sizing: 20% margin per trade</span>
                <span>Leverage: fixed 20x</span>
                <span>Provider TPs: used when real, else ROI target</span>
                <span>Close at: 75% ROI on margin (fallback)</span>
                <span>Breakeven: off</span>
                <span>Liquidation-risk filter: on</span>
                <span>Fallback SL when missing: on</span>
                <span>Entry-SL band gate: on (±0.3%)</span>
                <span>Chart profit-zone fallback: on</span>
              </div>
            </div>
          )}

          {form.strategy_preset === "trade_talks_v2" && (
            <div className="col-span-2 rounded-md border border-indigo-300 bg-indigo-50 p-3 space-y-2"
                 data-testid="ch-strategy-locked-summary">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-indigo-900">Trade Talks Strategy V2 — active</p>
                <Button
                  type="button" size="sm" variant="ghost"
                  className="h-6 px-2 text-[11px] text-indigo-700 hover:bg-indigo-100"
                  data-testid="ch-unlock-strategy"
                  onClick={() => setForm((prev) => ({ ...prev, strategy_preset: null }))}
                >
                  Unlock to customize
                </Button>
              </div>
              <p className="text-[11px] text-indigo-800">
                These fields are locked to the V2 config so they can't drift by accident.
                Unlock to edit them individually — the settings below (auto-trading, chart OCR,
                fallback SL%, circuit breakers) are still yours to adjust either way.
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono text-indigo-900 pt-1 border-t border-indigo-200">
                <span>Sizing: 15% risk per trade</span>
                <span>Leverage: 20x (auto-lowered if SL needs it)</span>
                <span>Provider TPs: used when real, else ROI target</span>
                <span>Close at: 30% ROI on margin (fallback)</span>
                <span>Breakeven: at 30% ROI, +2% offset</span>
                <span>Close 100% at first TP: off (partial only)</span>
                <span>Wide-SL trades: delever to fit, not skipped</span>
                <span>Fallback SL when missing: on</span>
                <span>Entry-SL band gate: on (±0.3%)</span>
                <span>Chart profit-zone fallback: on</span>
              </div>
            </div>
          )}

          {form.strategy_preset === "trade_talks_v3" && (
            <div className="col-span-2 rounded-md border border-violet-300 bg-violet-50 p-3 space-y-2"
                 data-testid="ch-strategy-locked-summary">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-violet-900">Trade Talks Strategy V3 — active</p>
                <Button
                  type="button" size="sm" variant="ghost"
                  className="h-6 px-2 text-[11px] text-violet-700 hover:bg-violet-100"
                  data-testid="ch-unlock-strategy"
                  onClick={() => setForm((prev) => ({ ...prev, strategy_preset: null }))}
                >
                  Unlock to customize
                </Button>
              </div>
              <p className="text-[11px] text-violet-800">
                These fields are locked to the V3 config so they can't drift by accident.
                Unlock to edit them individually — the settings below (auto-trading, chart OCR,
                fallback SL%, circuit breakers) are still yours to adjust either way.
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono text-violet-900 pt-1 border-t border-violet-200">
                <span>Sizing: 20% risk per trade</span>
                <span>Leverage: 25x (auto-lowered if SL needs it)</span>
                <span>Provider TPs: always ignored</span>
                <span>Close at: 50% ROI on margin, full close</span>
                <span>Breakeven trail: off</span>
                <span>Entry/SL: chart-box convention</span>
                <span>Wide-SL trades: delever to fit, not skipped</span>
                <span>Fallback SL when missing: on</span>
                <span>Entry-SL band gate: off</span>
              </div>
            </div>
          )}

          {form.strategy_preset === "trade_sensei" && (
            <div className="col-span-2 rounded-md border border-sky-300 bg-sky-50 p-3 space-y-2"
                 data-testid="ch-strategy-locked-summary">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-sky-900">Trade Sensei Strategy — active</p>
                <Button
                  type="button" size="sm" variant="ghost"
                  className="h-6 px-2 text-[11px] text-sky-700 hover:bg-sky-100"
                  data-testid="ch-unlock-strategy"
                  onClick={() => setForm((prev) => ({ ...prev, strategy_preset: null }))}
                >
                  Unlock to customize
                </Button>
              </div>
              <p className="text-[11px] text-sky-800">
                Layered DCA entry + provider TP waterfall with SL trailing, per the channel's own
                documented rules. Unlock to edit these individually — the settings below
                (auto-trading, chart OCR, fallback SL%, circuit breakers) are still yours either way.
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono text-sky-900 pt-1 border-t border-sky-200">
                <span>Sizing: 30% margin per trade</span>
                <span>Leverage: fixed 10x</span>
                <span>Entry: layered 70/30 (EP1/EP2)</span>
                <span>Exit: TP1 50% · TP2 30% · TP3 rest</span>
                <span>SL trail: BE at TP1, TP1 at TP2</span>
                <span>Don't chase: skip if TP1 already hit</span>
                <span>Liquidation-risk filter: on</span>
                <span>Fallback SL when missing: on</span>
              </div>
            </div>
          )}

          {form.strategy_preset === "trade_sensei_v2" && (
            <div className="col-span-2 rounded-md border border-violet-300 bg-violet-50 p-3 space-y-2"
                 data-testid="ch-strategy-locked-summary">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-violet-900">Trade Sensei Strategy V2 — active</p>
                <Button
                  type="button" size="sm" variant="ghost"
                  className="h-6 px-2 text-[11px] text-violet-700 hover:bg-violet-100"
                  data-testid="ch-unlock-strategy"
                  onClick={() => setForm((prev) => ({ ...prev, strategy_preset: null }))}
                >
                  Unlock to customize
                </Button>
              </div>
              <p className="text-[11px] text-violet-800">
                Same layered DCA entry + TP waterfall as V1, tuned from backtesting this channel's own
                history: 12x leverage (was 10x) and stop moved to breakeven once a trade hits 50% ROI.
                Unlock to edit these individually — the settings below (auto-trading, chart OCR,
                fallback SL%, circuit breakers) are still yours either way.
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono text-violet-900 pt-1 border-t border-violet-200">
                <span>Sizing: 30% margin per trade</span>
                <span>Leverage: 12x (auto-lowered if SL needs it)</span>
                <span>Entry: layered 70/30 (EP1/EP2)</span>
                <span>Exit: TP1 50% · TP2 30% · TP3 rest</span>
                <span>Breakeven: at 50% ROI, +0% offset</span>
                <span>SL trail: BE at TP1, TP1 at TP2</span>
                <span>Don't chase: skip if TP1 already hit</span>
                <span>Wide-SL trades: delever to fit, not skipped</span>
              </div>
            </div>
          )}

          {form.strategy_preset === "trade_sensei_v3" && (
            <div className="col-span-2 rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2"
                 data-testid="ch-strategy-locked-summary">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-amber-900">Trade Sensei Strategy V3 — active</p>
                <Button
                  type="button" size="sm" variant="ghost"
                  className="h-6 px-2 text-[11px] text-amber-700 hover:bg-amber-100"
                  data-testid="ch-unlock-strategy"
                  onClick={() => setForm((prev) => ({ ...prev, strategy_preset: null }))}
                >
                  Unlock to customize
                </Button>
              </div>
              <p className="text-[11px] text-amber-800">
                Same layered DCA entry, 12x leverage, and breakeven-at-50%-ROI as V2, but the
                provider's own TP is never used as the exit — every trade closes at a flat 100% ROI
                target instead, except one that got deleveraged to 6x or below (a wide SL forced
                it), which closes at 50% ROI instead since it needs a much bigger price move for the
                same return. Unlock to edit these individually — the settings below (auto-trading,
                chart OCR, fallback SL%, circuit breakers) are still yours either way.
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono text-amber-900 pt-1 border-t border-amber-200">
                <span>Sizing: 30% margin per trade</span>
                <span>Leverage: 12x (auto-lowered if SL needs it)</span>
                <span>Entry: layered 70/30 (EP1/EP2)</span>
                <span>Exit: always ROI target, provider TP ignored</span>
                <span>ROI target: 100% (50% if deleveraged to ≤6x)</span>
                <span>Breakeven: at 50% ROI, +0% offset</span>
                <span>SL trail: BE at TP1, TP1 at TP2</span>
                <span>Don't chase: skip if TP1 already hit</span>
              </div>
            </div>
          )}

          {form.strategy_preset === "trade_sensei_v3_2" && (
            <div className="col-span-2 rounded-md border border-rose-300 bg-rose-50 p-3 space-y-2"
                 data-testid="ch-strategy-locked-summary">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-rose-900">Trade Sensei Strategy V3.2 — active</p>
                <Button
                  type="button" size="sm" variant="ghost"
                  className="h-6 px-2 text-[11px] text-rose-700 hover:bg-rose-100"
                  data-testid="ch-unlock-strategy"
                  onClick={() => setForm((prev) => ({ ...prev, strategy_preset: null }))}
                >
                  Unlock to customize
                </Button>
              </div>
              <p className="text-[11px] text-rose-800">
                Same layered 70/30 DCA entry and 12x leverage as V3, but NOT a flat ROI-only
                exit — the provider's own TP is honored for TP2/TP3, only TP1 is replaced
                with a synthetic 115% ROI target. Found via a backtest sensitivity sweep
                across TP1 thresholds (50%–125% ROI); 115% was the best-performing value on
                this channel's own signal history. Unlock to edit these individually — the
                settings below (auto-trading, chart OCR, fallback SL%, circuit breakers)
                are still yours either way.
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono text-rose-900 pt-1 border-t border-rose-200">
                <span>Sizing: 30% margin per trade</span>
                <span>Leverage: 12x (auto-lowered if SL needs it)</span>
                <span>Entry: layered 70/30 (EP1/EP2)</span>
                <span>Exit: TP1 synthetic 115% ROI · TP2/TP3 real provider prices</span>
                <span>Breakeven: at 50% ROI, +0% offset</span>
                <span>SL trail: BE at TP1, TP1 at TP2</span>
                <span>Wide-SL trades: delever to fit, not skipped</span>
                <span>Don't chase: skip if TP1 already hit</span>
              </div>
            </div>
          )}

          {form.strategy_preset === "trade_sensei_v3_3" && (
            <div className="col-span-2 rounded-md border border-fuchsia-300 bg-fuchsia-50 p-3 space-y-2"
                 data-testid="ch-strategy-locked-summary">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-fuchsia-900">Trade Sensei Strategy V3.3 — active</p>
                <Button
                  type="button" size="sm" variant="ghost"
                  className="h-6 px-2 text-[11px] text-fuchsia-700 hover:bg-fuchsia-100"
                  data-testid="ch-unlock-strategy"
                  onClick={() => setForm((prev) => ({ ...prev, strategy_preset: null }))}
                >
                  Unlock to customize
                </Button>
              </div>
              <p className="text-[11px] text-fuchsia-800">
                Same layered 70/30 entry, 12x leverage, and TP1-synthetic-115%-ROI exit as
                V3.2, but two changes found via a concurrency-corrected backtest sweep:
                margin sizing lowered from 30% to 20% per trade, and wide-SL trades are no
                longer deleveraged or skipped — they keep full 12x leverage regardless. On
                this channel's own signal history that beats V3.2 on both return AND
                drawdown at the same time (not a tradeoff): +1303.69% vs V3.2's +1071.78%,
                while cutting max drawdown from 50.47% to 35.71%. Unlock to edit these
                individually.
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono text-fuchsia-900 pt-1 border-t border-fuchsia-200">
                <span>Sizing: 20% margin per trade</span>
                <span>Leverage: fixed 12x, never delevered</span>
                <span>Entry: layered 70/30 (EP1/EP2)</span>
                <span>Exit: TP1 synthetic 115% ROI · TP2/TP3 real provider prices</span>
                <span>Breakeven: at 50% ROI, +0% offset</span>
                <span>SL trail: BE at TP1, TP1 at TP2</span>
                <span>Wide-SL trades: not deleveraged or skipped</span>
                <span>Don't chase: skip if TP1 already hit</span>
              </div>
            </div>
          )}

          {form.strategy_preset === "trade_sensei_v3_4" && (
            <div className="col-span-2 rounded-md border border-orange-300 bg-orange-50 p-3 space-y-2"
                 data-testid="ch-strategy-locked-summary">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-orange-900">Trade Sensei Strategy V3.4 — active</p>
                <Button
                  type="button" size="sm" variant="ghost"
                  className="h-6 px-2 text-[11px] text-orange-700 hover:bg-orange-100"
                  data-testid="ch-unlock-strategy"
                  onClick={() => setForm((prev) => ({ ...prev, strategy_preset: null }))}
                >
                  Unlock to customize
                </Button>
              </div>
              <p className="text-[11px] text-orange-800">
                Same as V3.3 (20% margin, layered 70/30 entry, TP1-synthetic-115%-ROI exit,
                wide-SL trades never deleveraged or skipped) but leverage raised from 12x to
                30x. Found via a leverage sweep on top of V3.3's own concurrency-corrected
                backtest — non-monotonic across 20x/25x/30x (25x was actually worse on
                drawdown than 20x), but 30x came out best on BOTH return and drawdown at
                once: +7428.46% vs V3.3's +1303.69%, while cutting max drawdown from 35.71%
                to 23.03%. Most "losing" trades at this config are breakeven scratches (SL
                trailed to BE before getting hit), not real losses — only 5 of 66 backtested
                trades were genuine losses. Unlock to edit these individually.
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono text-orange-900 pt-1 border-t border-orange-200">
                <span>Sizing: 20% margin per trade</span>
                <span>Leverage: fixed 30x, never delevered</span>
                <span>Entry: layered 70/30 (EP1/EP2)</span>
                <span>Exit: TP1 synthetic 115% ROI · TP2/TP3 real provider prices</span>
                <span>Breakeven: at 50% ROI, +0% offset</span>
                <span>SL trail: BE at TP1, TP1 at TP2</span>
                <span>Wide-SL trades: not deleveraged or skipped</span>
                <span>Don't chase: skip if TP1 already hit</span>
              </div>
            </div>
          )}

          {/* "gvrcalls_real_tp_v1" is the old ad-hoc id this channel's config was
              originally saved under by a one-off script, before this formal UI preset
              existed — same config, alias it so the locked summary shows immediately
              instead of falling through to nothing (neither this block's exact-match
              nor the `!form.strategy_preset` picker below matched a non-null-but-
              unrecognized value). */}
          {(form.strategy_preset === "gvrcalls_strategy_v1"
            || form.strategy_preset === "gvrcalls_real_tp_v1") && (
            <div className="col-span-2 rounded-md border border-cyan-300 bg-cyan-50 p-3 space-y-2"
                 data-testid="ch-strategy-locked-summary">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-cyan-900">GVRCALLS Strategy V1 — active</p>
                <Button
                  type="button" size="sm" variant="ghost"
                  className="h-6 px-2 text-[11px] text-cyan-700 hover:bg-cyan-100"
                  data-testid="ch-unlock-strategy"
                  onClick={() => setForm((prev) => ({ ...prev, strategy_preset: null }))}
                >
                  Unlock to customize
                </Button>
              </div>
              <p className="text-[11px] text-cyan-800">
                Real TP1/TP2/TP3 waterfall (provider's own targets, never a flat synthetic
                ROI shortcut) with SL trailed to breakeven at TP1 and to TP1's price at TP2.
                Fixed 8x cross-margin leverage, 20% margin per trade. Wide-SL trades get
                deleveraged to fit rather than skipped. Backtested full-history
                (concurrency-corrected): +434.05% return, 76.06% win rate. Unlock to edit
                these individually — the
                settings below (auto-trading, chart OCR, fallback SL%, circuit breakers,
                margin mode, virtual balance) are still yours to adjust either way.
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono text-cyan-900 pt-1 border-t border-cyan-200">
                <span>Sizing: 20% margin per trade</span>
                <span>Leverage: fixed 8x</span>
                <span>Exit: real TP1 50% · TP2 30% · TP3 rest</span>
                <span>SL trail: BE at TP1, TP1 at TP2</span>
                <span>Wide-SL trades: delever to fit, not skipped</span>
                <span>Don't chase: skip if TP1 already hit</span>
                <span>Fallback SL when missing: on</span>
                <span>Auto-close ROI / independent BE trigger: off</span>
              </div>
            </div>
          )}

          {form.strategy_preset === "gvrcalls_strategy_v2" && (
            <div className="col-span-2 rounded-md border border-teal-300 bg-teal-50 p-3 space-y-2"
                 data-testid="ch-strategy-locked-summary">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-teal-900">GVRCALLS Strategy V2 — active</p>
                <Button
                  type="button" size="sm" variant="ghost"
                  className="h-6 px-2 text-[11px] text-teal-700 hover:bg-teal-100"
                  data-testid="ch-unlock-strategy"
                  onClick={() => setForm((prev) => ({ ...prev, strategy_preset: null }))}
                >
                  Unlock to customize
                </Button>
              </div>
              <p className="text-[11px] text-teal-800">
                Superseded by V3 — see below. Same real TP1/TP2/TP3 waterfall as V1, but
                sized differently: uses the provider's own stated leverage (20x on every
                GVRCALLS signal, confirmed) instead of a fixed 8x, and sizes the position
                so a stop-loss hit costs exactly 20% of current balance. Its headline
                return figure has been revised multiple times as bigger backtest
                methodology bugs were found and fixed (naive sequential-credit bias, then
                an isolated-margin-style loss cap that didn't match this account's real
                cross-margin behavior) — V3 is built on the corrected model and adds a
                real-money-verified improvement on top, so prefer V3 unless you have a
                specific reason to use this exact V2 config. Unlock to edit individually.
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono text-teal-900 pt-1 border-t border-teal-200">
                <span>Sizing: risk-based, 20% of balance lost if SL hits</span>
                <span>Leverage: provider's own (20x), uncapped by delever</span>
                <span>Exit: real TP1 50% · TP2 30% · TP3 rest</span>
                <span>SL trail: BE at TP1, TP1 at TP2</span>
                <span>Wide-SL trades: not deleveraged or skipped</span>
                <span>Don't chase: skip if TP1 already hit</span>
                <span>Fallback SL when missing: on</span>
                <span>Margin mode: cross</span>
              </div>
            </div>
          )}

          {form.strategy_preset === "gvrcalls_strategy_v3" && (
            <div className="col-span-2 rounded-md border border-indigo-300 bg-indigo-50 p-3 space-y-2"
                 data-testid="ch-strategy-locked-summary">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-indigo-900">GVRCALLS Strategy V3 — active</p>
                <Button
                  type="button" size="sm" variant="ghost"
                  className="h-6 px-2 text-[11px] text-indigo-700 hover:bg-indigo-100"
                  data-testid="ch-unlock-strategy"
                  onClick={() => setForm((prev) => ({ ...prev, strategy_preset: null }))}
                >
                  Unlock to customize
                </Button>
              </div>
              <p className="text-[11px] text-indigo-800">
                Same as V2 (provider's real 20x leverage, risk-based sizing, real TP1/TP2/TP3
                waterfall, wide-SL trades never deleveraged or skipped) plus two changes found
                via a combinatorial backtest search on the corrected cross-margin loss model
                (GVRCALLS runs cross margin — losses aren't capped at nominal margin the way
                isolated margin would be, verified against a real live trade): SL no longer
                trails to breakeven the instant TP1 is touched (it still trails to TP1's price
                once TP2 hits), and a 100%-ROI safety-net close runs alongside the real
                waterfall — whichever threshold price reaches first wins. Backtested
                full-history: +558.95% return, 10.56% max drawdown, 87% win rate (at 10% risk
                sizing) — cross-checked against GVRCALLS' own self-reported "Lost on $X" tweets
                (only ~2.5% of backtest-classified wins contradicted, far better than an
                earlier flat-ROI-only approach that was rejected for a ~20% contradiction
                rate). Unlock to edit these individually.
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono text-indigo-900 pt-1 border-t border-indigo-200">
                <span>Sizing: risk-based, X% of balance lost if SL hits</span>
                <span>Leverage: provider's own (20x), uncapped by delever</span>
                <span>Exit: real TP1 50% · TP2 30% · TP3 rest, OR 100% ROI safety net</span>
                <span>SL trail: NOT at TP1 · trails to TP1's price at TP2</span>
                <span>Wide-SL trades: not deleveraged or skipped</span>
                <span>Don't chase: skip if TP1 already hit</span>
                <span>Fallback SL when missing: on</span>
                <span>Margin mode: cross</span>
              </div>
            </div>
          )}

          {!form.strategy_preset && (
            <div className="col-span-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 space-y-2">
              <p className="text-sm font-medium text-emerald-900">Strategy presets</p>
              <div className="space-y-1.5 opacity-50">
                <p className="text-[11px] text-emerald-800">
                  <span className="font-semibold uppercase tracking-wide">Deprecated — superseded by V3.</span>{" "}
                  Trade Talks Strategy — 20% margin sizing, fixed 20x leverage, closes at 75% ROI on
                  margin when no real TP exists (a genuine provider TP always wins when the signal has
                  one), breakeven off, and skips any entry whose stop-loss is wider than 20x can
                  survive.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled
                  className="border-emerald-400 text-emerald-800"
                  data-testid="ch-apply-trade-talks-strategy"
                >
                  Apply Trade Talks Strategy (disabled)
                </Button>
              </div>
              <div className="space-y-1.5 pt-2 border-t border-emerald-200 opacity-50">
                <p className="text-[11px] text-indigo-800">
                  <span className="font-semibold uppercase tracking-wide">Deprecated — superseded by V3.</span>{" "}
                  Trade Talks Strategy V2 — risk-based sizing at 15% of balance per trade, 20x leverage
                  by default, moves stop to breakeven once a trade is 30% ROI in profit, force-closes at
                  30% ROI if still open.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled
                  className="border-indigo-400 text-indigo-800"
                  data-testid="ch-apply-trade-talks-v2-strategy"
                >
                  Apply Trade Talks Strategy V2 (disabled)
                </Button>
              </div>
              <div className="space-y-1.5 pt-2 border-t border-emerald-200">
                <p className="text-[11px] text-violet-800">
                  Trade Talks Strategy V3 — risk-based sizing at 20% of balance per trade (loses that
                  much if the SL hits), 25x leverage, auto-delevered down (never skipped) when a
                  signal's stop-loss is too wide for 25x to survive. Full close at 50% ROI on margin,
                  every time — the provider's own stated take-profit is never used as the exit, on
                  purpose: backtesting showed a disciplined fixed-ROI target outperforms chasing
                  whatever target the provider happened to post. Entry/SL are read from the provider's
                  chart convention: for a signal with no separately stated stop-loss, the far edge of
                  its 2-price range is the real SL, not a second entry price.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-violet-400 text-violet-800 hover:bg-violet-100"
                  data-testid="ch-apply-trade-talks-v3-strategy"
                  onClick={() => setForm((prev) => ({
                    ...prev,
                    position_sizing_mode: "risk",
                    risk_percent_per_trade: 20,
                    follow_provider_leverage: false,
                    default_leverage: 25,
                    max_leverage: 25,
                    auto_delever_to_fit_sl: true,
                    auto_close_roi_pct: 50,
                    ignore_provider_tp_always: true,
                    ignore_provider_tp: false,
                    use_implied_tp_zone_when_no_explicit_tp: false,
                    close_full_at_tp1: true,
                    skip_if_sl_beyond_liquidation: false,
                    execute_without_provider_sl: true,
                    layered_entry_enabled: false,
                    tp_waterfall_enabled: false,
                    skip_if_tp1_already_hit: false,
                    entry_sl_band_gate_enabled: false,
                    sl_to_breakeven_at_roi_pct: 0,
                    sl_to_breakeven_offset_pct: 0,
                    strategy_preset: "trade_talks_v3",
                  }))}
                >
                  Apply Trade Talks Strategy V3
                </Button>
              </div>
              <div className="space-y-1.5 pt-2 border-t border-emerald-200">
                <p className="text-[11px] text-emerald-800">
                  Trade Sensei Strategy — layered 70/30 entry (DCA), TP1/TP2/TP3 waterfall closing
                  50%/30%/rest with SL trailed to breakeven at TP1 and to TP1 at TP2, 30% margin at
                  fixed 10x leverage, don't-chase filter, liquidation-risk filter. Backtested on 7
                  real months of signals: 85%+ win rate vs. -87.6% under a mismatched 20x/ROI-only
                  config.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-sky-400 text-sky-800 hover:bg-sky-100"
                  data-testid="ch-apply-trade-sensei-strategy"
                  onClick={() => setForm((prev) => ({
                    ...prev,
                    position_sizing_mode: "margin",
                    margin_percent_per_trade: 30,
                    follow_provider_leverage: false,
                    default_leverage: 10,
                    max_leverage: 10,
                    ignore_provider_tp: false,
                    auto_close_roi_pct: 0,
                    use_implied_tp_zone_when_no_explicit_tp: false,
                    sl_to_breakeven_at_roi_pct: 0,
                    sl_to_breakeven_offset_pct: 0,
                    close_full_at_tp1: false,
                    skip_if_sl_beyond_liquidation: true,
                    execute_without_provider_sl: true,
                    layered_entry_enabled: true,
                    layered_entry_pct1: 70,
                    tp_waterfall_enabled: true,
                    tp1_close_pct: 50,
                    tp2_close_pct: 30,
                    sl_trail_to_be_at_tp1: true,
                    sl_trail_to_tp1_at_tp2: true,
                    skip_if_tp1_already_hit: true,
                    strategy_preset: "trade_sensei",
                  }))}
                >
                  Apply Trade Sensei Strategy
                </Button>
              </div>
              <div className="space-y-1.5 pt-2 border-t border-emerald-200">
                <p className="text-[11px] text-violet-800">
                  Trade Sensei Strategy V2 — same layered 70/30 entry and TP1/TP2/TP3 waterfall as V1,
                  tuned from backtesting this channel's own signal history: 12x leverage instead of
                  10x, stop moved to breakeven once a trade reaches 50% ROI (instead of never), and
                  wide-SL signals get leverage auto-lowered for that one trade instead of being
                  skipped outright.
                  On this channel's Jan–Jul 2026 history that turned a 33% win rate / -49% drawdown
                  into 42% win rate / -40% drawdown, with a meaningfully higher ending balance.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-violet-400 text-violet-800 hover:bg-violet-100"
                  data-testid="ch-apply-trade-sensei-v2-strategy"
                  onClick={() => setForm((prev) => ({
                    ...prev,
                    position_sizing_mode: "margin",
                    margin_percent_per_trade: 30,
                    follow_provider_leverage: false,
                    default_leverage: 12,
                    max_leverage: 12,
                    ignore_provider_tp: false,
                    auto_close_roi_pct: 0,
                    use_implied_tp_zone_when_no_explicit_tp: false,
                    sl_to_breakeven_at_roi_pct: 50,
                    sl_to_breakeven_offset_pct: 0,
                    close_full_at_tp1: false,
                    skip_if_sl_beyond_liquidation: true,
                    auto_delever_to_fit_sl: true,
                    execute_without_provider_sl: true,
                    layered_entry_enabled: true,
                    layered_entry_pct1: 70,
                    tp_waterfall_enabled: true,
                    tp1_close_pct: 50,
                    tp2_close_pct: 30,
                    sl_trail_to_be_at_tp1: true,
                    sl_trail_to_tp1_at_tp2: true,
                    skip_if_tp1_already_hit: true,
                    strategy_preset: "trade_sensei_v2",
                  }))}
                >
                  Apply Trade Sensei Strategy V2
                </Button>
              </div>
              <div className="space-y-1.5 pt-2 border-t border-emerald-200">
                <p className="text-[11px] text-amber-800">
                  Trade Sensei Strategy V3 — same layered 70/30 entry, 12x leverage, and
                  breakeven-at-50%-ROI as V2, but ignores the provider's own TP entirely and always
                  closes at a flat 100% ROI target — except a trade that got deleveraged to 6x or
                  below (wide SL forced it), which closes at 50% ROI instead since that trade needs a
                  much bigger price move for the same return.
                  On this channel's full history that turned $100 into $2,053 (closed trades only,
                  still-open positions excluded) vs. V2's $486 and a flat-100%-ROI-for-everyone
                  variant's $1,755.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-amber-400 text-amber-800 hover:bg-amber-100"
                  data-testid="ch-apply-trade-sensei-v3-strategy"
                  onClick={() => setForm((prev) => ({
                    ...prev,
                    position_sizing_mode: "margin",
                    margin_percent_per_trade: 30,
                    follow_provider_leverage: false,
                    default_leverage: 12,
                    max_leverage: 12,
                    ignore_provider_tp: false,
                    auto_close_roi_pct: 100,
                    ignore_provider_tp_always: true,
                    use_implied_tp_zone_when_no_explicit_tp: false,
                    sl_to_breakeven_at_roi_pct: 50,
                    sl_to_breakeven_offset_pct: 0,
                    close_full_at_tp1: false,
                    skip_if_sl_beyond_liquidation: true,
                    auto_delever_to_fit_sl: true,
                    execute_without_provider_sl: true,
                    layered_entry_enabled: true,
                    layered_entry_pct1: 70,
                    tp_waterfall_enabled: true,
                    tp1_close_pct: 50,
                    tp2_close_pct: 30,
                    sl_trail_to_be_at_tp1: true,
                    sl_trail_to_tp1_at_tp2: true,
                    skip_if_tp1_already_hit: true,
                    low_leverage_roi_threshold: 6,
                    low_leverage_roi_pct: 50,
                    strategy_preset: "trade_sensei_v3",
                  }))}
                >
                  Apply Trade Sensei Strategy V3
                </Button>
              </div>
              <div className="space-y-1.5 pt-2 border-t border-emerald-200">
                <p className="text-[11px] text-rose-800">
                  Trade Sensei Strategy V3.2 — same layered 70/30 entry and 12x leverage as
                  V3, but NOT flat-ROI-only: TP1 is a synthetic 115% ROI target, TP2/TP3 use
                  the provider's own real prices. Found via a sensitivity sweep across TP1
                  ROI thresholds (50%-125%) on this channel's own signal history — 115% won.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-rose-400 text-rose-800 hover:bg-rose-100"
                  data-testid="ch-apply-trade-sensei-v3-2-strategy"
                  onClick={() => setForm((prev) => ({
                    ...prev,
                    position_sizing_mode: "margin",
                    margin_percent_per_trade: 30,
                    follow_provider_leverage: false,
                    default_leverage: 12,
                    max_leverage: 12,
                    ignore_provider_tp: false,
                    ignore_provider_tp_always: false,
                    tp1_custom_roi_pct: 115,
                    auto_close_roi_pct: 0,
                    use_implied_tp_zone_when_no_explicit_tp: false,
                    sl_to_breakeven_at_roi_pct: 50,
                    sl_to_breakeven_offset_pct: 0,
                    close_full_at_tp1: false,
                    skip_if_sl_beyond_liquidation: true,
                    auto_delever_to_fit_sl: true,
                    execute_without_provider_sl: true,
                    layered_entry_enabled: true,
                    layered_entry_pct1: 70,
                    tp_waterfall_enabled: true,
                    tp1_close_pct: 50,
                    tp2_close_pct: 30,
                    sl_trail_to_be_at_tp1: true,
                    sl_trail_to_tp1_at_tp2: true,
                    skip_if_tp1_already_hit: true,
                    strategy_preset: "trade_sensei_v3_2",
                  }))}
                >
                  Apply Trade Sensei Strategy V3.2
                </Button>
              </div>
              <div className="space-y-1.5 pt-2 border-t border-emerald-200">
                <p className="text-[11px] text-fuchsia-800">
                  Trade Sensei Strategy V3.3 — same layered 70/30 entry, 12x leverage, and
                  TP1-synthetic-115%-ROI exit as V3.2, but sizing lowered to 20% margin per
                  trade and wide-SL trades no longer deleveraged or skipped (keep full 12x
                  regardless). Found via a concurrency-corrected backtest sweep — beats V3.2
                  on both return AND drawdown at once: +1303.69% vs +1071.78%, while cutting
                  max drawdown from 50.47% to 35.71%.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-fuchsia-400 text-fuchsia-800 hover:bg-fuchsia-100"
                  data-testid="ch-apply-trade-sensei-v3-3-strategy"
                  onClick={() => setForm((prev) => ({
                    ...prev,
                    position_sizing_mode: "margin",
                    margin_percent_per_trade: 20,
                    follow_provider_leverage: false,
                    default_leverage: 12,
                    max_leverage: 12,
                    ignore_provider_tp: false,
                    ignore_provider_tp_always: false,
                    tp1_custom_roi_pct: 115,
                    auto_close_roi_pct: 0,
                    use_implied_tp_zone_when_no_explicit_tp: false,
                    sl_to_breakeven_at_roi_pct: 50,
                    sl_to_breakeven_offset_pct: 0,
                    close_full_at_tp1: false,
                    skip_if_sl_beyond_liquidation: false,
                    auto_delever_to_fit_sl: false,
                    execute_without_provider_sl: true,
                    layered_entry_enabled: true,
                    layered_entry_pct1: 70,
                    tp_waterfall_enabled: true,
                    tp1_close_pct: 50,
                    tp2_close_pct: 30,
                    sl_trail_to_be_at_tp1: true,
                    sl_trail_to_tp1_at_tp2: true,
                    skip_if_tp1_already_hit: true,
                    strategy_preset: "trade_sensei_v3_3",
                  }))}
                >
                  Apply Trade Sensei Strategy V3.3
                </Button>
              </div>
              <div className="space-y-1.5 pt-2 border-t border-emerald-200">
                <p className="text-[11px] text-orange-800">
                  Trade Sensei Strategy V3.4 — same as V3.3 (20% margin, layered 70/30 entry,
                  TP1 synthetic 115% ROI, wide-SL trades never deleveraged or skipped) but
                  leverage raised from 12x to 30x. Found via a leverage sweep on top of
                  V3.3's own backtest — beats V3.3 on both return AND drawdown at once:
                  +7428.46% vs +1303.69%, while cutting max drawdown from 35.71% to 23.03%.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-orange-400 text-orange-800 hover:bg-orange-100"
                  data-testid="ch-apply-trade-sensei-v3-4-strategy"
                  onClick={() => setForm((prev) => ({
                    ...prev,
                    position_sizing_mode: "margin",
                    margin_percent_per_trade: 20,
                    follow_provider_leverage: false,
                    default_leverage: 30,
                    max_leverage: 30,
                    ignore_provider_tp: false,
                    ignore_provider_tp_always: false,
                    tp1_custom_roi_pct: 115,
                    auto_close_roi_pct: 0,
                    use_implied_tp_zone_when_no_explicit_tp: false,
                    sl_to_breakeven_at_roi_pct: 50,
                    sl_to_breakeven_offset_pct: 0,
                    close_full_at_tp1: false,
                    skip_if_sl_beyond_liquidation: false,
                    auto_delever_to_fit_sl: false,
                    execute_without_provider_sl: true,
                    layered_entry_enabled: true,
                    layered_entry_pct1: 70,
                    tp_waterfall_enabled: true,
                    tp1_close_pct: 50,
                    tp2_close_pct: 30,
                    sl_trail_to_be_at_tp1: true,
                    sl_trail_to_tp1_at_tp2: true,
                    skip_if_tp1_already_hit: true,
                    strategy_preset: "trade_sensei_v3_4",
                  }))}
                >
                  Apply Trade Sensei Strategy V3.4
                </Button>
              </div>
              <div className="space-y-1.5 pt-2 border-t border-emerald-200">
                <p className="text-[11px] text-cyan-800">
                  GVRCALLS Strategy V1 — real TP1/TP2/TP3 waterfall (provider's own targets,
                  never a flat synthetic ROI shortcut), SL trailed to breakeven at TP1 and to
                  TP1's price at TP2, fixed 8x cross-margin leverage, 20% margin per trade,
                  wide-SL trades deleveraged to fit rather than skipped. Backtested
                  full-history (concurrency-corrected, not the naive sequential figure):
                  +434.05% return, 76.06% win rate.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-cyan-400 text-cyan-800 hover:bg-cyan-100"
                  data-testid="ch-apply-gvrcalls-v1-strategy"
                  onClick={() => setForm((prev) => ({
                    ...prev,
                    position_sizing_mode: "margin",
                    margin_percent_per_trade: 20,
                    follow_provider_leverage: false,
                    default_leverage: 8,
                    max_leverage: 8,
                    ignore_provider_tp: false,
                    auto_close_roi_pct: 0,
                    use_implied_tp_zone_when_no_explicit_tp: false,
                    sl_to_breakeven_at_roi_pct: 0,
                    sl_to_breakeven_offset_pct: 0,
                    close_full_at_tp1: false,
                    skip_if_sl_beyond_liquidation: true,
                    execute_without_provider_sl: true,
                    allow_provider_sl_replace: true,
                    allow_wider_provider_sl: false,
                    layered_entry_enabled: false,
                    tp_waterfall_enabled: true,
                    tp1_close_pct: 50,
                    tp2_close_pct: 30,
                    sl_trail_to_be_at_tp1: true,
                    sl_trail_to_tp1_at_tp2: true,
                    skip_if_tp1_already_hit: true,
                    strategy_preset: "gvrcalls_strategy_v1",
                  }))}
                >
                  Apply GVRCALLS Strategy V1
                </Button>
              </div>
              <div className="space-y-1.5 pt-2 border-t border-emerald-200">
                <p className="text-[11px] text-teal-800">
                  GVRCALLS Strategy V2 — same real TP1/TP2/TP3 waterfall as V1, but uses the
                  provider's own stated leverage (20x, confirmed uniform across every
                  GVRCALLS signal) instead of a fixed 8x, and sizes risk-based so a
                  stop-loss hit costs exactly 20% of balance instead of putting a flat 20%
                  up as margin regardless of leverage. Wide-SL trades are no longer
                  deleveraged or skipped since risk-based sizing already targets the loss
                  directly. Superseded by V3 below — its headline return figure has been
                  revised as bigger backtest methodology bugs were found (see V3's
                  description); use V3 unless you need this exact V2 config specifically.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-teal-400 text-teal-800 hover:bg-teal-100"
                  data-testid="ch-apply-gvrcalls-v2-strategy"
                  onClick={() => setForm((prev) => ({
                    ...prev,
                    position_sizing_mode: "risk",
                    risk_percent_per_trade: 20,
                    follow_provider_leverage: true,
                    default_leverage: 20,
                    max_leverage: 20,
                    ignore_provider_tp: false,
                    auto_close_roi_pct: 0,
                    use_implied_tp_zone_when_no_explicit_tp: false,
                    sl_to_breakeven_at_roi_pct: 0,
                    sl_to_breakeven_offset_pct: 0,
                    close_full_at_tp1: false,
                    skip_if_sl_beyond_liquidation: false,
                    auto_delever_to_fit_sl: false,
                    execute_without_provider_sl: true,
                    allow_provider_sl_replace: true,
                    allow_wider_provider_sl: false,
                    layered_entry_enabled: false,
                    tp_waterfall_enabled: true,
                    tp1_close_pct: 50,
                    tp2_close_pct: 30,
                    sl_trail_to_be_at_tp1: true,
                    sl_trail_to_tp1_at_tp2: true,
                    skip_if_tp1_already_hit: true,
                    strategy_preset: "gvrcalls_strategy_v2",
                  }))}
                >
                  Apply GVRCALLS Strategy V2
                </Button>
              </div>
              <div className="space-y-1.5 pt-2 border-t border-emerald-200">
                <p className="text-[11px] text-indigo-800">
                  GVRCALLS Strategy V3 — same as V2, plus two changes found via a
                  combinatorial search on the CORRECTED cross-margin loss model (GVRCALLS
                  runs cross margin, so losses aren't capped at nominal margin the way
                  isolated margin would be — verified against a real live trade): SL no
                  longer trails to breakeven the instant TP1 is touched, and a 100%-ROI
                  safety-net close runs alongside the real TP1/TP2/TP3 waterfall. Backtested
                  full-history: +558.95% return, 10.56% max drawdown, 87% win rate (at 10%
                  risk) — cross-checked against GVRCALLS' own "Lost on $X" self-reports
                  (~2.5% contradiction rate, far better than a rejected flat-ROI-only
                  approach's ~20%).
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-indigo-400 text-indigo-800 hover:bg-indigo-100"
                  data-testid="ch-apply-gvrcalls-v3-strategy"
                  onClick={() => setForm((prev) => ({
                    ...prev,
                    position_sizing_mode: "risk",
                    risk_percent_per_trade: 20,
                    follow_provider_leverage: true,
                    default_leverage: 20,
                    max_leverage: 20,
                    ignore_provider_tp: false,
                    auto_close_roi_pct: 100,
                    use_implied_tp_zone_when_no_explicit_tp: false,
                    sl_to_breakeven_at_roi_pct: 0,
                    sl_to_breakeven_offset_pct: 0,
                    close_full_at_tp1: false,
                    skip_if_sl_beyond_liquidation: false,
                    auto_delever_to_fit_sl: false,
                    execute_without_provider_sl: true,
                    allow_provider_sl_replace: true,
                    allow_wider_provider_sl: false,
                    layered_entry_enabled: false,
                    tp_waterfall_enabled: true,
                    tp1_close_pct: 50,
                    tp2_close_pct: 30,
                    sl_trail_to_be_at_tp1: false,
                    sl_trail_to_tp1_at_tp2: true,
                    skip_if_tp1_already_hit: true,
                    strategy_preset: "gvrcalls_strategy_v3",
                  }))}
                >
                  Apply GVRCALLS Strategy V3
                </Button>
              </div>
            </div>
          )}

          <div className="col-span-2 space-y-3">
            <ToggleRow label="Auto-trading" desc="Execute parsed signals automatically"
                       checked={!!form.auto_trading} onChange={setF("auto_trading")} testId="ch-risk-auto-trading" />
            {!form.strategy_preset && (
              <ToggleRow label="Execute without provider SL" desc="Attach internal fallback SL if missing"
                         checked={!!form.execute_without_provider_sl} onChange={setF("execute_without_provider_sl")} testId="ch-risk-exec-no-sl" />
            )}
            <ToggleRow label="Allow provider SL to replace internal" desc="Move to provider SL when it arrives"
                       checked={!!form.allow_provider_sl_replace} onChange={setF("allow_provider_sl_replace")} testId="ch-risk-replace" />
            <ToggleRow label="Allow wider provider SL" desc="Off by default — dangerous"
                       checked={!!form.allow_wider_provider_sl} onChange={setF("allow_wider_provider_sl")} testId="ch-risk-wider" />
            <ToggleRow label="Require manual approval for AI signals"
                       desc="AI-parsed signals wait in a queue for human approve/reject (regex-parsed signals still auto-execute)"
                       checked={!!form.require_manual_approval_ai_signals}
                       onChange={setF("require_manual_approval_ai_signals")}
                       testId="ch-risk-manual-approval-ai" />

            {!form.strategy_preset && (
              <>
                <ToggleRow label="Follow provider leverage" desc="Use signal's leverage when specified (still capped by max)"
                           checked={!!form.follow_provider_leverage} onChange={setF("follow_provider_leverage")} testId="ch-risk-follow-lev" />
                <ToggleRow label="Close 100% at TP1" desc="Book the full trade on first TP hit (vs 33% partial)"
                           checked={!!form.close_full_at_tp1} onChange={setF("close_full_at_tp1")} testId="ch-risk-close-tp1" />
                {/* ============ EXIT STRATEGY (consolidated) ============ */}
                <ExitStrategyBlock form={form} setForm={setForm} />
              </>
            )}

            <ToggleRow
              label="Extract signals from chart images (Claude vision)"
              desc="Runs OCR on chart-only messages so image-based signals become tradeable. Costs ~1 LLM call per unique chart; reposts are cached free."
              checked={!!form.enable_chart_ocr}
              onChange={setF("enable_chart_ocr")}
              testId="ch-risk-enable-chart-ocr" />

            {!form.strategy_preset && (
              <>
                <PositionSizingBlock form={form} setForm={setForm} />

                <SliderRow label={`Default leverage (when signal has none) — ${form.default_leverage ?? "-"}x`}
                           value={[Number(form.default_leverage) || 0]}
                           onValueChange={(v) => setForm({ ...form, default_leverage: v[0] })}
                           min={1} max={50} step={1} testId="ch-risk-default-lev" />
                <SliderRow label={`Max leverage (hard cap) — ${form.max_leverage ?? "-"}x`}
                           value={[Number(form.max_leverage) || 0]}
                           onValueChange={(v) => setForm({ ...form, max_leverage: v[0] })}
                           min={1} max={125} step={1} testId="ch-risk-max-lev" />
              </>
            )}

            <VirtualBalanceBlock form={form} setForm={setForm} channel={channel} onReset={resetVirtualBalance} />

            <SliderRow label={`Internal fallback SL — ${form.internal_fallback_sl_percent ?? "-"}%`}
                       value={[Number(form.internal_fallback_sl_percent) || 0]}
                       onValueChange={(v) => setForm({ ...form, internal_fallback_sl_percent: v[0] })}
                       min={0.1} max={20} step={0.1} testId="ch-risk-fallback-sl" />
          </div>

          {/* ============ GO-LIVE SAFETY / CIRCUIT BREAKERS (channel-scoped) ============ */}
          <div className="col-span-2 mt-4 pt-4 border-t border-slate-200 space-y-3"
               data-testid="ch-section-circuit-breakers">
            <div>
              <p className="overline text-red-700">🛡 Go-live safety</p>
              <p className="text-sm font-medium mt-1">Circuit breakers</p>
              <p className="text-[11px] text-slate-500">
                Hard limits that protect your account from cascading losses & runaway orders. Use 0 = unlimited.
              </p>
            </div>

            <SliderRow label={`Hard leverage ceiling (absolute cap) — ${form.hard_leverage_ceiling ?? "-"}x`}
                       value={[Number(form.hard_leverage_ceiling) || 0]}
                       onValueChange={(v) => setForm({ ...form, hard_leverage_ceiling: v[0] })}
                       min={1} max={125} step={1} testId="ch-risk-hard-lev-ceiling" />
            <SliderRow label={`Max concurrent open positions — ${form.max_concurrent_positions || "unlimited"}`}
                       value={[Number(form.max_concurrent_positions) || 0]}
                       onValueChange={(v) => setForm({ ...form, max_concurrent_positions: v[0] })}
                       min={0} max={20} step={1} testId="ch-risk-max-concurrent" />
            <SliderRow label={`Per-trade notional cap (USDT) — ${form.max_notional_per_trade_usdt ? `$${form.max_notional_per_trade_usdt}` : "unlimited"} · use for canary/first-live runs`}
                       value={[Number(form.max_notional_per_trade_usdt) || 0]}
                       onValueChange={(v) => setForm({ ...form, max_notional_per_trade_usdt: v[0] })}
                       min={0} max={1000} step={5} testId="ch-risk-notional-cap" />
            <SliderRow label={`Daily loss kill-switch — ${form.daily_loss_pct_kill_switch ? `-${form.daily_loss_pct_kill_switch}%` : "disabled"}`}
                       value={[Number(form.daily_loss_pct_kill_switch) || 0]}
                       onValueChange={(v) => setForm({ ...form, daily_loss_pct_kill_switch: v[0] })}
                       min={0} max={20} step={0.5} testId="ch-risk-daily-kill" />
            <SliderRow label={`Same-symbol cooldown — ${form.symbol_cooldown_seconds ? `${form.symbol_cooldown_seconds}s` : "off"}`}
                       value={[Number(form.symbol_cooldown_seconds) || 0]}
                       onValueChange={(v) => setForm({ ...form, symbol_cooldown_seconds: v[0] })}
                       min={0} max={3600} step={30} testId="ch-risk-symbol-cooldown" />
            <SliderRow label={`Duplicate-message window — ${form.dedupe_window_seconds ? `${form.dedupe_window_seconds}s` : "off"}`}
                       value={[Number(form.dedupe_window_seconds) || 0]}
                       onValueChange={(v) => setForm({ ...form, dedupe_window_seconds: v[0] })}
                       min={0} max={3600} step={30} testId="ch-risk-dedupe-window" />
            <SliderRow label={`Max entry slippage — ${form.max_entry_slippage_pct ? `${form.max_entry_slippage_pct}%` : "off (any drift accepted)"} · reject trade if market drifted more than this adverse`}
                       value={[Number(form.max_entry_slippage_pct) || 0]}
                       onValueChange={(v) => setForm({ ...form, max_entry_slippage_pct: v[0] })}
                       min={0} max={2} step={0.05} testId="ch-risk-max-slippage" />
            <ToggleRow label="Place exchange-side stop order (live mode)"
                       desc="Puts a real conditional SL on the exchange so protection survives app crashes"
                       checked={form.place_exchange_sl_order !== false}
                       onChange={setF("place_exchange_sl_order")}
                       testId="ch-risk-place-exch-sl" />
            {!form.strategy_preset && (
              <ToggleRow label="Skip entry if SL exceeds liquidation"
                         desc="Refuse the trade when stop_distance% × leverage > 100% - the exchange would liquidate before that stop is ever reached"
                         checked={!!form.skip_if_sl_beyond_liquidation}
                         onChange={setF("skip_if_sl_beyond_liquidation")}
                         testId="ch-risk-skip-liq" />
            )}
            <ToggleRow label="Replace pending limit order on updated signal"
                       desc="If a new signal arrives for a symbol that already has an unfilled resting limit order (not an open position), cancel the old order and place the new one instead of blocking it"
                       checked={!!form.replace_pending_limit_on_new_signal}
                       onChange={setF("replace_pending_limit_on_new_signal")}
                       testId="ch-risk-replace-pending-limit" />
            <ToggleRow label="Telegram alerts"
                       desc="DM your Saved Messages / alerts channel when kill switch fires or a trade errors"
                       checked={form.telegram_alerts_enabled !== false}
                       onChange={setF("telegram_alerts_enabled")}
                       testId="ch-risk-telegram-alerts" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="ch-cancel">Cancel</Button>
          <Button
            disabled={save.isPending || !form.name.trim()}
            onClick={() => save.mutate()}
            data-testid="ch-save"
          >
            {save.isPending ? "Saving…" : isNew ? "Create" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============ POSITION SIZING (risk-based vs margin-based) ============
   risk mode: same $ lost if SL hits every trade; margin/notional floats
   with stop width. margin mode: same margin committed every trade; $ risk
   at SL floats with stop width instead. See calculate_position_size in
   trade_engine.py for the exact math both modes share. */
function PositionSizingBlock({ form, setForm }) {
  const mode = ["margin", "fixed_margin"].includes(form.position_sizing_mode)
    ? form.position_sizing_mode : "risk";

  const modes = [
    { id: "risk", label: "Risk-based",
      hint: "Same $ lost if SL hits, every trade. Margin used varies with stop width." },
    { id: "margin", label: "Margin-based",
      hint: "Same margin committed every trade. $ risk at SL varies with stop width." },
    { id: "fixed_margin", label: "Fixed margin ($)",
      hint: "Same flat dollar amount of margin every trade, regardless of balance." },
  ];

  return (
    <div className="rounded-md border border-slate-200 bg-white/60 p-4 space-y-3"
         data-testid="ch-sizing-mode-block">
      <div>
        <p className="overline text-slate-700">Position sizing</p>
        <p className="text-[11px] text-slate-500 mt-1">How trade size is calculated from your balance.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {modes.map((m) => (
          <button
            key={m.id}
            type="button"
            data-testid={`ch-sizing-mode-${m.id}`}
            onClick={() => setForm({ ...form, position_sizing_mode: m.id })}
            className={
              "text-left p-3 rounded-md border transition-colors " +
              (mode === m.id
                ? "border-primary bg-primary/5"
                : "border-slate-200 bg-white hover:border-slate-400")
            }
          >
            <p className={"text-sm font-medium " + (mode === m.id ? "text-primary" : "text-slate-900")}>
              {m.label}
            </p>
            <p className={"text-[11px] mt-0.5 " + (mode === m.id ? "text-primary/70" : "text-slate-500")}>
              {m.hint}
            </p>
          </button>
        ))}
      </div>

      {mode === "risk" ? (
        <SliderRow label={`% of balance risked per trade — ${form.risk_percent_per_trade ?? "-"}%`}
                   value={[Number(form.risk_percent_per_trade) || 0]}
                   onValueChange={(v) => setForm({ ...form, risk_percent_per_trade: v[0] })}
                   min={0.1} max={20} step={0.1} testId="ch-risk-percent" />
      ) : mode === "fixed_margin" ? (
        <div>
          <Label>Fixed margin per trade (USDT)</Label>
          <Input
            type="number"
            min={1}
            step={1}
            data-testid="ch-fixed-margin-usdt"
            value={form.fixed_margin_usdt}
            onChange={(e) => setForm({ ...form, fixed_margin_usdt: e.target.value })}
          />
          <p className="text-[11px] text-slate-500 mt-1">
            Same flat dollar margin committed on every trade for this channel, regardless of account balance.
          </p>
        </div>
      ) : (
        <SliderRow label={`% of balance as margin per trade — ${form.margin_percent_per_trade ?? "-"}%`}
                   value={[Number(form.margin_percent_per_trade) || 0]}
                   onValueChange={(v) => setForm({ ...form, margin_percent_per_trade: v[0] })}
                   min={0.1} max={50} step={0.1} testId="ch-margin-percent" />
      )}
    </div>
  );
}

/* ============ VIRTUAL PER-CHANNEL BALANCE ============
   Opt-in tracked balance separate from the real shared exchange balance —
   position sizing for THIS channel compounds off its own real $ trade P&L
   instead of the real account balance. Does NOT protect capital (the
   underlying exchange balance stays genuinely commingled) — only gives
   independent sizing growth per channel. virtual_current_balance and
   virtual_balance_paused are server-maintained (see
   trade_engine._apply_channel_virtual_pnl) — this block only writes
   virtual_balance_enabled/virtual_starting_balance via the normal Save
   button; the "Reset balance" action below is a separate, explicit mutation
   so it never fires accidentally alongside an unrelated field edit. */
function VirtualBalanceBlock({ form, setForm, channel, onReset }) {
  const [resetValue, setResetValue] = useState(
    form.virtual_current_balance ?? form.virtual_starting_balance ?? 100
  );
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const enabled = !!form.virtual_balance_enabled;
  const isExisting = !!channel;

  return (
    <div className="rounded-md border border-slate-200 bg-white/60 p-4 space-y-3"
         data-testid="ch-virtual-balance-block">
      <ToggleRow
        label="Virtual per-channel balance"
        desc="Size this channel's trades off its own tracked balance instead of the real WEEX account balance. Grows/shrinks with this channel's own real $ P&L only — does not isolate capital."
        checked={enabled}
        onChange={(v) => setForm({ ...form, virtual_balance_enabled: v })}
        testId="ch-virtual-balance-enabled"
      />

      {enabled && (
        <>
          <div>
            <Label>Starting balance (USDT)</Label>
            <Input
              type="number"
              min={0}
              step={1}
              data-testid="ch-virtual-starting-balance"
              value={form.virtual_starting_balance}
              onChange={(e) => setForm({ ...form, virtual_starting_balance: e.target.value })}
            />
            {form.virtual_current_balance == null && (
              <p className="text-[11px] text-slate-500 mt-1">
                Saved as the channel's starting virtual balance the first time you enable this.
              </p>
            )}
          </div>

          {isExisting && (
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Current virtual balance</span>
                <span className="text-sm font-medium text-slate-900" data-testid="ch-virtual-current-balance">
                  {form.virtual_current_balance != null
                    ? `$${Number(form.virtual_current_balance).toFixed(2)}`
                    : "— (not started yet)"}
                </span>
              </div>
              {form.virtual_balance_paused && (
                <p className="text-[11px] text-red-600 font-medium">
                  Paused — balance hit $0 or below. New trades on this channel are skipped
                  until you reset/top up below.
                </p>
              )}
              <div className="flex items-end gap-2 pt-1">
                <div className="flex-1">
                  <Label className="text-xs">Reset / top up to</Label>
                  <Input
                    type="number"
                    step={1}
                    data-testid="ch-virtual-reset-value"
                    value={resetValue}
                    onChange={(e) => setResetValue(e.target.value)}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="ch-virtual-reset-btn"
                  onClick={() => onReset(Number(resetValue) || 0)}
                >
                  Reset balance
                </Button>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full"
                data-testid="ch-virtual-view-ledger-btn"
                onClick={() => setLedgerOpen(true)}
              >
                View ledger
              </Button>
            </div>
          )}
        </>
      )}
      {ledgerOpen && (
        <VirtualLedgerDialog channel={channel} onClose={() => setLedgerOpen(false)} />
      )}
    </div>
  );
}

/* Per-trade running-balance history for a virtual-balance-enabled channel —
   reconstructed server-side (GET /channels/{id}/virtual-balance-ledger) from
   every real closed trade's realized_pnl, cumulative from the starting
   balance. Read-only audit view; matches the methodology already used ad hoc
   this session to verify Trade Sensei Premium/GVRCALLS by hand. */
function VirtualLedgerDialog({ channel, onClose }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["virtual-ledger", channel?.id],
    queryFn: () => channels.virtualLedger(channel.id).then((r) => r.data),
    enabled: !!channel?.id,
  });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Virtual balance ledger — {channel?.name}</DialogTitle>
          <DialogDescription>
            Running virtual balance across every real closed trade on this channel, oldest first.
          </DialogDescription>
        </DialogHeader>

        {isLoading && <p className="text-sm text-slate-500">Loading…</p>}
        {isError && <p className="text-sm text-red-600">Failed to load the ledger.</p>}

        {data && (
          <>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <Kv k="Starting balance" v={`$${Number(data.virtual_starting_balance).toFixed(2)}`} />
              <Kv k="Current balance" v={
                data.virtual_current_balance != null
                  ? `$${Number(data.virtual_current_balance).toFixed(2)}`
                  : "—"
              } />
              <Kv k="Trades" v={data.trade_count} />
            </div>

            <div className="flex-1 overflow-auto border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Closed</TableHead>
                    <TableHead>Symbol</TableHead>
                    <TableHead>Side</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">Realized PnL</TableHead>
                    <TableHead className="text-right">Balance before</TableHead>
                    <TableHead className="text-right">Balance after</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.ledger.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-slate-500 py-6">
                        No closed real trades yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {data.ledger.map((row) => (
                    <TableRow key={row.trade_id}>
                      <TableCell className="text-xs whitespace-nowrap">
                        {row.closed_at ? new Date(row.closed_at).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell>{row.symbol}</TableCell>
                      <TableCell className="capitalize">{row.side}</TableCell>
                      <TableCell className="text-xs">{row.close_reason}</TableCell>
                      <TableCell className={cn(
                        "text-right font-medium",
                        row.realized_pnl >= 0 ? "text-emerald-600" : "text-red-600"
                      )}>
                        {row.realized_pnl >= 0 ? "+" : ""}${Number(row.realized_pnl).toFixed(4)}
                      </TableCell>
                      <TableCell className="text-right">${Number(row.virtual_balance_before).toFixed(2)}</TableCell>
                      <TableCell className="text-right">${Number(row.virtual_balance_after).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============ EXIT STRATEGY (unified) ============
   Consolidates 5 legacy controls (auto_close_roi_pct, ignore_provider_tp,
   close_full_at_tp1, sl_to_breakeven_at_roi_pct, sl_to_breakeven_offset_pct)
   into a single radio-picker + progressive-disclosure block. */
function ExitStrategyBlock({ form, setForm }) {
  // Derive current strategy from flags. Priority: hold_roi > full_at_tp1 > hybrid > provider.
  const currentStrategy =
    form.ignore_provider_tp ? "hold_roi"
      : form.close_full_at_tp1 ? "full_at_tp1"
      : (Number(form.auto_close_roi_pct) || 0) > 0 ? "hybrid"
      : "provider";

  const setStrategy = (s) => {
    setForm({
      ...form,
      ignore_provider_tp: s === "hold_roi",
      close_full_at_tp1: s === "full_at_tp1",
      // Reset ROI target when switching to a mode that doesn't need it
      auto_close_roi_pct:
        s === "hold_roi" ? (form.auto_close_roi_pct || 100)
          : s === "hybrid" ? (form.auto_close_roi_pct || 50)
          : 0,
    });
  };

  const strategies = [
    { id: "provider",    label: "Provider TPs (33% partial closes)",
      hint: "Use TP1/TP2/... from the signal exactly as posted." },
    { id: "full_at_tp1", label: "Full close at TP1",
      hint: "Book 100% of the trade the moment TP1 hits — no partial." },
    { id: "hold_roi",    label: "Hold until ROI target",
      hint: "Use the ROI target — unless the provider actually gives a real TP, which always wins when present." },
    { id: "hybrid",      label: "Hybrid (provider TPs OR ROI target)",
      hint: "Whichever fires first — provider TP1 or your ROI target." },
  ];

  const roiVisible = currentStrategy === "hold_roi" || currentStrategy === "hybrid";
  const [advOpen, setAdvOpen] = useState(false);

  return (
    <div className="rounded-md border border-slate-200 bg-white/60 p-4 space-y-3"
         data-testid="ch-exit-strategy-block">
      <div>
        <p className="overline text-slate-700">Exit strategy</p>
        <p className="text-[11px] text-slate-500 mt-1">How the trade closes when it&apos;s in profit. SL is set separately.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {strategies.map((s) => (
          <button
            key={s.id}
            type="button"
            data-testid={`ch-exit-strategy-${s.id}`}
            onClick={() => setStrategy(s.id)}
            className={
              "text-left p-3 rounded-md border transition-colors " +
              (currentStrategy === s.id
                ? "border-primary bg-primary/5"
                : "border-slate-200 bg-white hover:border-slate-400")
            }
          >
            <p className={"text-sm font-medium " + (currentStrategy === s.id ? "text-primary" : "text-slate-900")}>
              {s.label}
            </p>
            <p className={"text-[11px] mt-0.5 " + (currentStrategy === s.id ? "text-primary/70" : "text-slate-500")}>
              {s.hint}
            </p>
          </button>
        ))}
      </div>

      {roiVisible && (
        <div className="pt-2 border-t border-slate-200" data-testid="ch-exit-roi-slider">
          <SliderRow
            label={`ROI target — +${form.auto_close_roi_pct || 0}%`}
            value={[Number(form.auto_close_roi_pct) || 0]}
            onValueChange={(v) => setForm({ ...form, auto_close_roi_pct: v[0] })}
            min={10} max={500} step={5}
            testId="ch-exit-roi-slider-input"
          />
          <p className="text-[10px] text-slate-500 mt-1">
            ROI is measured on margin used. At 5x leverage, +50% ROI = +10% price move in your favor.
            A real provider TP always overrides this when the signal actually has one.
          </p>
          <ToggleRow
            label="Use chart's unlabeled profit zone when no real TP exists"
            desc="If the provider's chart shows a shaded target-direction zone with no price label (and no real TP anywhere), use its midpoint instead of the flat ROI target above — capped so it can never ask for a bigger move than the ROI target would."
            checked={!!form.use_implied_tp_zone_when_no_explicit_tp}
            onChange={(v) => setForm({ ...form, use_implied_tp_zone_when_no_explicit_tp: v })}
            testId="ch-exit-use-implied-tp-zone"
          />
        </div>
      )}

      {/* Advanced: Move-SL-to-BE (independent from exit strategy) */}
      <button
        type="button"
        onClick={() => setAdvOpen((v) => !v)}
        className="text-xs text-slate-600 hover:text-slate-900 underline underline-offset-2"
        data-testid="ch-exit-advanced-toggle"
      >
        {advOpen ? "Hide" : "Show"} advanced — move SL to breakeven
      </button>

      {advOpen && (
        <div className="space-y-3 pt-2 border-t border-slate-200"
             data-testid="ch-exit-advanced-block">
          <SliderRow
            label={
              form.sl_to_breakeven_at_roi_pct
                ? `Move SL to breakeven at +${form.sl_to_breakeven_at_roi_pct}% ROI (fires once)`
                : "Move SL to breakeven — disabled"
            }
            value={[Number(form.sl_to_breakeven_at_roi_pct) || 0]}
            onValueChange={(v) => setForm({ ...form, sl_to_breakeven_at_roi_pct: v[0] })}
            min={0} max={500} step={5}
            testId="ch-exit-be-roi-threshold"
          />
          <SliderRow
            label={
              form.sl_to_breakeven_offset_pct
                ? `BE offset — ${form.sl_to_breakeven_offset_pct}% ${form.sl_to_breakeven_offset_pct > 0 ? "past entry (covers fees)" : "before entry"}`
                : "BE offset — 0% (exact entry)"
            }
            value={[Number(form.sl_to_breakeven_offset_pct) || 0]}
            onValueChange={(v) => setForm({ ...form, sl_to_breakeven_offset_pct: v[0] })}
            min={-0.5} max={2} step={0.05}
            testId="ch-exit-be-offset"
          />
        </div>
      )}
    </div>
  );
}


/* ============ SIGNALS ============ */
export function PendingAiApprovalCard() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["signals-pending"],
    queryFn: async () => (await signals.listPending()).data,
    refetchInterval: 4000,
  });
  const approve = useMutation({
    mutationFn: (id) => signals.approve(id),
    onSuccess: (r) => {
      toast.success(`Approved & executed (${r.data.engine?.action || "queued"})`);
      qc.invalidateQueries();
    },
    onError: (e) => toast.error("Approve failed: " + (e.response?.data?.detail || e.message)),
  });
  const reject = useMutation({
    mutationFn: (id) => signals.reject(id),
    onSuccess: () => { toast.info("Signal rejected"); qc.invalidateQueries(); },
    onError: (e) => toast.error("Reject failed: " + (e.response?.data?.detail || e.message)),
  });

  const items = q.data || [];
  if (items.length === 0) return null;

  return (
    <div className="card-surface p-6 border-2 border-amber-300 bg-amber-50/40 lg:col-span-2"
         data-testid="section-pending-approval">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="overline text-amber-700">⏳ Awaiting human review</p>
          <h3 className="font-display text-xl mt-1">AI Signals — Manual Approval Queue</h3>
          <p className="text-xs text-slate-600 mt-1">
            These signals were parsed by Claude AI. Approve to execute, reject to discard.
          </p>
        </div>
        <Badge variant="warn" data-testid="pending-count">{items.length} pending</Badge>
      </div>
      <ScrollArea className="max-h-[500px] pr-3">
        <div className="space-y-3">
          {items.map((p) => (
            <div key={p.id} className="p-4 rounded-md bg-white border border-amber-200"
                 data-testid={`pending-signal-${p.id}`}>
              <div className="flex items-center gap-2 text-xs mb-2 flex-wrap">
                <Badge variant="info">{p.action_type}</Badge>
                {p.symbol && <Badge variant="muted">{p.symbol}</Badge>}
                {p.side && <Badge variant={p.side === "long" ? "profit" : "loss"}>{p.side}</Badge>}
                <Badge variant="warn">🤖 AI</Badge>
                <span className="ml-auto mono text-[10px] text-slate-500">
                  conf {(p.confidence * 100).toFixed(0)}% · {new Date(p.created_at).toLocaleTimeString()}
                </span>
              </div>
              {p.raw_text && (
                <pre className="mono text-xs text-slate-700 whitespace-pre-wrap bg-slate-50 p-2 rounded mb-2 border border-slate-200">
{p.raw_text}
                </pre>
              )}
              {p.ai_reasoning && (
                <div className="p-2 rounded-md bg-amber-50 border border-amber-200 mb-2">
                  <p className="text-[10px] uppercase tracking-widest text-amber-600 mb-1">🤖 AI reasoning</p>
                  <p className="text-xs text-slate-800 italic">{p.ai_reasoning}</p>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2 text-xs mono">
                <Kv k="Entry" v={p.entry_prices?.join(", ") || "—"} />
                <Kv k="SL" v={p.stop_loss ?? "—"} />
                <Kv k="TP" v={p.take_profits?.join(", ") || "—"} />
                <Kv k="Lev" v={p.leverage ?? "—"} />
                <Kv k="Partial" v={p.partial_percent ? `${p.partial_percent}%` : "—"} />
                <Kv k="Channel" v={p.raw_channel || "—"} />
              </div>
              <div className="flex gap-2 mt-3">
                <Button
                  data-testid={`approve-signal-${p.id}`}
                  onClick={() => approve.mutate(p.id)}
                  disabled={approve.isPending}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
                >
                  ✓ Approve & Execute
                </Button>
                <Button
                  data-testid={`reject-signal-${p.id}`}
                  onClick={() => reject.mutate(p.id)}
                  disabled={reject.isPending}
                  size="sm"
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50 flex-1"
                >
                  ✕ Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

export function ReparseStuckButton() {
  const qc = useQueryClient();
  const [preview, setPreview] = useState(null);
  const dry = useMutation({
    mutationFn: () => trading.reparseStuck(true),
    onSuccess: (r) => setPreview(r.data),
    onError: (e) => toast.error("Preview failed: " + (e.response?.data?.detail || e.message)),
  });
  const apply = useMutation({
    mutationFn: () => trading.reparseStuck(false),
    onSuccess: (r) => {
      toast.success(`Reclassified ${r.data.would_change} signal${r.data.would_change !== 1 ? "s" : ""}`);
      setPreview(null);
      qc.invalidateQueries();
    },
    onError: (e) => toast.error("Reparse failed: " + (e.response?.data?.detail || e.message)),
  });

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          data-testid="reparse-stuck-btn"
          size="sm"
          variant="outline"
          className="text-xs"
          onClick={() => { dry.mutate(); }}
        >
          🔄 Re-parse stuck
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="reparse-stuck-dialog" className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Re-parse historical signals</AlertDialogTitle>
          <AlertDialogDescription>
            Runs the current parser over previously-parsed signals and updates any that
            would now be classified differently (e.g. entries that were stuck as
            <code className="mx-1 px-1 rounded bg-slate-100 font-mono text-xs">partial_close</code>
            due to the old TP1: regex bug). This does <strong>not</strong> re-execute trades — it only
            fixes the historical audit trail.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {dry.isPending && <p className="text-sm text-slate-500 py-4">Scanning...</p>}

        {preview && (
          <div className="my-3 p-3 rounded-md bg-slate-50 border border-slate-200 max-h-[300px] overflow-auto"
               data-testid="reparse-preview">
            <p className="text-sm font-medium">
              Checked <span className="mono">{preview.checked}</span> · Would update{" "}
              <span className="mono text-amber-700 font-semibold">{preview.would_change}</span>
            </p>
            {preview.samples?.length > 0 ? (
              <table className="w-full mt-3 text-xs">
                <thead className="text-slate-500 text-[10px] uppercase tracking-wider">
                  <tr><th className="text-left py-1">Was</th><th className="text-left py-1">Now</th><th className="text-left py-1">Text</th></tr>
                </thead>
                <tbody>
                  {preview.samples.map((s) => (
                    <tr key={s.signal_id} className="border-t border-slate-200">
                      <td className="py-1 pr-2"><Badge variant="muted">{s.was}</Badge></td>
                      <td className="py-1 pr-2"><Badge variant="info">{s.now}</Badge></td>
                      <td className="py-1 mono text-slate-700 truncate max-w-md">{s.text_preview}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-xs text-slate-500 mt-2">No signals need reclassification. You&apos;re all clean.</p>
            )}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="reparse-cancel-btn">Close</AlertDialogCancel>
          <AlertDialogAction
            data-testid="reparse-apply-btn"
            onClick={(e) => { e.preventDefault(); apply.mutate(); }}
            disabled={apply.isPending || !preview || preview.would_change === 0}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {apply.isPending ? "Applying..." : `Apply ${preview?.would_change || 0} update${preview?.would_change === 1 ? "" : "s"}`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
